import { UserRole, canAccessManagement, canAccessPublic } from '@/domain/auth/auth'
import { Paths } from '@/routes/paths'
import { useAuthStore } from '@/store/useAuthStore'
import { useNavigate } from '@tanstack/react-router'
import { ReactNode, useEffect } from 'react'
import { LoaderElement } from './LoaderElement'
import { NotFound } from './NotFound'
import { RestrictedAccess } from './RestrictedAccess'

interface ProtectedRouteProps {
  children: ReactNode
  requiredRoles?: UserRole[]
  requireManagementAccess?: boolean
  requirePublicAccess?: boolean
  fallbackPath?: string
}

export const ProtectedRoute = ({
  children,
  requiredRoles,
  requireManagementAccess = false,
  requirePublicAccess = false,
  fallbackPath
}: ProtectedRouteProps) => {
  const { user, isAuthenticated, isLoading } = useAuthStore()
  const navigate = useNavigate()

  useEffect(() => {
    // If not loading and not authenticated, redirect to appropriate login
    if (!isLoading && !isAuthenticated) {
      const redirectPath = requireManagementAccess ? Paths.ManagementLogin : Paths.PublicLogin
      navigate({ to: fallbackPath || redirectPath })
      return
    }

    // If authenticated but doesn't have required access
    if (!isLoading && isAuthenticated && user) {
      // Check management access
      if (requireManagementAccess && !canAccessManagement(user)) {
        navigate({ to: fallbackPath || Paths.PublicLogin })
        return
      }

      // Check public access
      if (requirePublicAccess && !canAccessPublic(user)) {
        navigate({ to: fallbackPath || Paths.PublicDashboard })
        return
      }

      // Check specific roles
      if (requiredRoles && requiredRoles.length > 0) {
        const hasRequiredRole = requiredRoles.some(role => user.roles.includes(role))
        if (!hasRequiredRole) {
          // const redirectPath = canAccessManagement(user) ? Paths.ManagementDashboard : Paths.PublicDashboard
          // navigate({ to: fallbackPath || redirectPath })
          return
        }
      }
    }
  }, [isLoading, isAuthenticated, user, requiredRoles, requireManagementAccess, requirePublicAccess, fallbackPath, navigate])

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoaderElement />
      </div>
    )
  }

  // If not authenticated, don't render children (redirect will happen in useEffect)
  if (!isAuthenticated) {
    return <NotFound />
  }

  // If authenticated but access checks are still pending, show loading
  if (user) {
    if (requireManagementAccess && !canAccessManagement(user)) {
      return <NotFound />
    }
    if (requirePublicAccess && !canAccessPublic(user)) {
      return <NotFound />
    }
    if (requiredRoles && requiredRoles.length > 0) {
      const hasRequiredRole = requiredRoles.some(role => user.roles.includes(role))
      if (!hasRequiredRole) {
        return <RestrictedAccess />
      }
    }
  }

  return <>{children}</>
}
